import type { PosterDoc } from "@studio/shared";

/**
 * 포스터 자동 저장(D17) — 기존 `lib/autosave.ts` 를 쓰지 않는다.
 *
 * 🚨 그 훅은 입력할 때마다 30초 타이머를 다시 건다 — 학생이 계속 고치는 동안 **서버 저장이 0회**다
 *    (설계 검토 실측). 포스터는 끌기·크기 조절로 1초에도 여러 번 바뀌므로 그대로 쓰면 한 차시 내내
 *    저장이 안 될 수 있다.
 *
 * 규칙
 *   · 2초 쉬면 저장한다. 그래도 계속 고치고 있으면 **처음 고친 뒤 5초** 에는 무조건 저장한다.
 *   · 저장은 한 번에 하나. 저장 중에 또 바뀌면 끝난 뒤 이어서 저장한다.
 *   · 각 저장은 **직전 응답의 revision** 을 base 로 보낸다 — 낡은 base 는 서버가 409 로 거절한다
 *     (두 번째 탭 · 다른 컴퓨터). 409 면 멈추고 화면에 알린다(덮어쓰지 않는다).
 *   · 네트워크 실패는 버리지 않고 5초 뒤 다시 보낸다.
 *   · 다시 보내도 소용없는 실패(422 등 — isPermanent)는 자동으로 다시 보내지 않는다. 다음 편집 때 다시 시도한다.
 *   · flushNow 는 한 번만 보내 보고, 실패하면 오류를 호출자에게 던진다(2026-10-05 검수 QA-02 —
 *     예전에는 실패를 삼키고 같은 문서를 간격 없이 다시 보내 500ms 에 127회 요청이 나갔다).
 */

type SaveOutcome = { ok: true } | { ok: false; error: unknown };
const OK: SaveOutcome = { ok: true };

export type PosterSaveState = "idle" | "pending" | "saving" | "saved" | "error" | "conflict";

export interface PosterSaverOptions {
  save: (doc: PosterDoc, baseRevision: number) => Promise<{ revision: number }>;
  onState?: (state: PosterSaveState, revision: number) => void;
  /** 409 STALE_REVISION — 다른 곳에서 고쳤다 */
  isConflict?: (error: unknown) => boolean;
  /** 다시 보내도 같은 결과인 실패(검증 422 등) — 자동 재시도하지 않는다 */
  isPermanent?: (error: unknown) => boolean;
  idleMs?: number;
  maxWaitMs?: number;
  retryMs?: number;
  now?: () => number;
}

export class PosterSaver {
  #revision: number;
  #pending: PosterDoc | null = null;
  #firstPendingAt: number | null = null;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #inFlight: Promise<SaveOutcome> | null = null;
  #stopped = false;
  /** 영구 실패 뒤 — 새 편집이 올 때까지 자동으로 다시 보내지 않는다 */
  #held = false;
  /** 화면을 떠났다 — 남은 것은 기다리지 않고 바로 보내고, 실패해도 다시 보내지 않는다 */
  #closing = false;
  readonly #options: Required<Omit<PosterSaverOptions, "onState" | "isConflict" | "isPermanent">>
    & Pick<PosterSaverOptions, "onState" | "isConflict" | "isPermanent">;

  constructor(initialRevision: number, options: PosterSaverOptions) {
    this.#revision = initialRevision;
    this.#options = {
      idleMs: 2_000,
      maxWaitMs: 5_000,
      retryMs: 5_000,
      now: () => Date.now(),
      ...options,
    };
  }

  get revision(): number {
    return this.#revision;
  }

  /** 서버에서 새로 읽어 왔을 때(충돌 뒤 다시 불러오기) */
  reset(revision: number): void {
    this.#revision = revision;
    this.#pending = null;
    this.#firstPendingAt = null;
    this.#stopped = false;
    this.#held = false;
    this.#clearTimer();
    this.#emit("idle");
  }

  update(doc: PosterDoc): void {
    if (this.#stopped) return;
    this.#held = false;
    this.#pending = doc;
    const now = this.#options.now();
    this.#firstPendingAt ??= now;
    this.#emit("pending");
    this.#schedule(now);
  }

  /**
   * 화면을 떠나기 전 · 다음 단계로 가기 전 — 남은 것을 바로 보내고 끝날 때까지 기다린다.
   * 저장이 실패하면 그 오류를 던진다(남은 문서는 버리지 않고, 일시 실패면 5초 뒤 재시도도 그대로 걸린다).
   */
  async flushNow(): Promise<void> {
    while (!this.#stopped && (this.#pending || this.#inFlight)) {
      const outcome = await (this.#inFlight ?? this.#flush());
      if (!outcome.ok) throw outcome.error;
    }
  }

  hasUnsaved(): boolean {
    return this.#pending !== null || this.#inFlight !== null;
  }

  /**
   * 편집 화면이 내려갈 때(앱 안 뒤로가기 · 다른 화면으로 이동) — 남은 편집을 버리지 않고 바로 보낸다.
   * 2026-10-05 검수 QA-03: 예전에는 dispose 가 2초 대기 타이머를 지워 마지막 편집이 사라졌다.
   */
  close(): void {
    this.#closing = true;
    this.#clearTimer();
    if (!this.#stopped && !this.#inFlight && this.#pending) void this.#flush();
    // 저장 중이면 끝난 뒤 finally 가 남은 것을 곧바로 보낸다.
  }

  /** 같은 화면이 다시 올라왔다(React StrictMode 의 개발용 이중 마운트) — close 를 되돌린다. */
  open(): void {
    this.#closing = false;
    if (!this.#stopped && !this.#held && this.#pending && !this.#inFlight) this.#schedule(this.#options.now());
  }

  dispose(): void {
    this.#stopped = true;
    this.#clearTimer();
  }

  #schedule(now: number): void {
    this.#clearTimer();
    if (this.#inFlight) return; // 저장이 끝나면 이어서 잡는다
    const waited = now - (this.#firstPendingAt ?? now);
    const delay = this.#closing ? 0 : Math.max(0, Math.min(this.#options.idleMs, this.#options.maxWaitMs - waited));
    this.#timer = setTimeout(() => void this.#flush(), delay);
  }

  #flush(): Promise<SaveOutcome> {
    this.#clearTimer();
    if (this.#inFlight) return this.#inFlight;
    if (this.#stopped || !this.#pending) return Promise.resolve(OK);
    const doc = this.#pending;
    this.#pending = null;
    this.#firstPendingAt = null;
    this.#emit("saving");
    const attempt: Promise<SaveOutcome> = this.#options.save(doc, this.#revision)
      .then((result): SaveOutcome => {
        this.#revision = result.revision;
        this.#emit(this.#pending ? "pending" : "saved");
        return OK;
      }, (error: unknown): SaveOutcome => {
        if (this.#options.isConflict?.(error)) {
          this.#stopped = true;
          this.#pending = null;
          this.#emit("conflict");
          return { ok: false, error };
        }
        // 버리지 않는다 — 그사이 새로 고친 게 없으면 이번 것을 다시 보낸다.
        this.#pending ??= doc;
        this.#firstPendingAt ??= this.#options.now();
        this.#emit("error");
        this.#clearTimer();
        if (this.#closing || this.#options.isPermanent?.(error)) this.#held = true;
        else this.#timer = setTimeout(() => void this.#flush(), this.#options.retryMs);
        return { ok: false, error };
      })
      .finally(() => {
        this.#inFlight = null;
        if (!this.#stopped && !this.#held && this.#pending && this.#timer === null) this.#schedule(this.#options.now());
      });
    this.#inFlight = attempt;
    return attempt;
  }

  #clearTimer(): void {
    if (this.#timer !== null) clearTimeout(this.#timer);
    this.#timer = null;
  }

  #emit(state: PosterSaveState): void {
    this.#options.onState?.(state, this.#revision);
  }
}
